import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useTranslation } from 'react-i18next';

const ProductList = ({ categoryId, title, description }) => {
  const { t } = useTranslation();

  // Fetch products from translation file based on categoryId
  // The translation file should have a structure like: products.categories.[categoryId].items
  const products = t(`products.categories.${categoryId}.items`, { returnObjects: true });

  // Ensure products is an array
  const productList = Array.isArray(products) ? products : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 text-center md:text-left">
          <Link to="/products" className="inline-block mb-6">
            <Button 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10 hover:text-white transition-all duration-300"
            >
              <ArrowRight className="mr-2 h-4 w-4 rotate-180" /> 
              {t('common.backToCategories') || 'Back to Categories'}
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            {title || t(`products.categories.${categoryId}.title`)}
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl leading-relaxed mx-auto md:mx-0">
            {description || t(`products.categories.${categoryId}.description`)}
          </p>
        </div>
      </section>

      <div className="max-w-[95%] sm:max-w-[90%] md:max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {productList.map((product, index) => (
            <Card
              key={index}
              className="overflow-hidden border-none shadow-lg hover:shadow-xl transition-all duration-300 group bg-white flex flex-col"
            >
              {/* Image Placeholder or Actual Image */}
              <div className="relative h-56 sm:h-64 overflow-hidden bg-gray-200">
                {product.image ? (
                  <img
                    src={process.env.PUBLIC_URL + product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                    <span className="text-sm">No Image Available</span>
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  <span className="bg-[#FF6A00] text-white px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg">
                    {t(`products.categories.${categoryId}.name`)}
                  </span>
                </div>
              </div>

              {/* Card Content */}
              <CardContent className="p-5 sm:p-6 flex-grow flex flex-col">
                <h3 className="text-lg sm:text-xl font-bold text-[#0A1F44] mb-2 sm:mb-3">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 line-clamp-3 flex-grow">
                  {product.description}
                </p>

                {/* Specs Preview (if available) */}
                {product.specs && (
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      {Object.entries(product.specs).slice(0, 4).map(([key, value]) => (
                        <div key={key}>
                          <span className="text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                          <p className="font-semibold text-[#0A1F44] truncate" title={value}>
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Link to={`/products/${categoryId}/${product.id || index}`} className="block mt-auto">
                  <Button className="w-full bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white transition-all duration-300 group text-sm sm:text-base py-2 sm:py-3">
                    {t('common.viewDetails') || 'View Details'}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {productList.length === 0 && (
          <div className="text-center py-16">
            <p className="text-lg sm:text-xl text-gray-500">
              {t('products.empty') || 'No products found in this category.'}
            </p>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <section className="bg-[#0A1F44] text-white py-12 sm:py-16 md:py-20 mt-8 sm:mt-12 text-center">
        <div className="max-w-[95%] sm:max-w-[90%] md:max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
            {t('products.cta.title')}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed">
            {t('products.cta.description')}
          </p>
          <a href="mailto:support@hh-enterprise.net">
            <Button
              size="lg"
              className="bg-[#FF6A00] hover:bg-[#E55F00] text-white px-6 py-3 text-sm sm:text-base transition-all duration-300"
            >
              {t('products.cta.button')} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductList;
